import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../constants";

/**
 * RefundActivityCard — last-N-days refund rollup for the CRM Dashboard.
 *
 * Surfaces three at-a-glance numbers: refund count, total $ refunded, and the
 * refund rate (refunds ÷ (refunds + paid) in window). When a discount code
 * appears on multiple refunds, a "most-refunded code" callout fires so admins
 * can spot abuse patterns (e.g. promo-redeem → chargeback cycles) early. A
 * one-click "Disable" button on the callout calls /api/admin/disable-discount-code
 * to block future redemptions of the abused code (existing lifetimes preserved).
 */
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function RefundActivityCard({ adminPassword }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [disabledCodes, setDisabledCodes] = useState(new Set());
  const [disablingCode, setDisablingCode] = useState(null);

  const refresh = useCallback(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/crm/refund-activity`, { params: { days } }),
      adminPassword
        ? axios.get(`${API}/admin/disabled-discount-codes`, { params: { key: adminPassword } }).catch(() => ({ data: { items: [] } }))
        : Promise.resolve({ data: { items: [] } }),
    ])
      .then(([r1, r2]) => {
        if (!alive) return;
        setData(r1.data);
        setDisabledCodes(new Set((r2.data.items || []).map((it) => it.code)));
      })
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [days, adminPassword]);

  useEffect(() => {
    return refresh();
  }, [refresh]);

  const disableCode = async (code) => {
    if (!adminPassword) {
      toast.error("Admin password required to disable a code.");
      return;
    }
    const reason = window.prompt(
      `Disable promo code "${code}" for new redemptions?\n\nExisting lifetime users who already redeemed it keep their access. New customers won't be able to use it.\n\nReason (optional):`,
      "Excessive refunds / suspected abuse"
    );
    if (reason === null) return;
    setDisablingCode(code);
    try {
      await axios.post(`${API}/admin/disable-discount-code`, {
        password: adminPassword,
        code,
        reason,
      });
      toast.success(`Code "${code}" disabled. New redemptions blocked.`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disable code");
    } finally {
      setDisablingCode(null);
    }
  };

  const enableCode = async (code) => {
    if (!adminPassword) return;
    if (!window.confirm(`Re-enable promo code "${code}" for new redemptions?`)) return;
    setDisablingCode(code);
    try {
      await axios.post(`${API}/admin/enable-discount-code`, {
        password: adminPassword,
        code,
      });
      toast.success(`Code "${code}" re-enabled.`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to re-enable code");
    } finally {
      setDisablingCode(null);
    }
  };

  // Refund rate severity → color: ≥10% red, ≥5% amber, else green
  const rate = data?.refund_rate_pct ?? 0;
  const rateColor = rate >= 10 ? "text-red-400" : rate >= 5 ? "text-amber-400" : "text-[#A3E635]";

  return (
    <div className="card-glass p-6 mb-6" data-testid="refund-activity-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-lg" aria-hidden="true">↩️</span>
          <h3 className="text-white font-semibold text-sm uppercase tracking-wider">Refund Activity</h3>
        </div>
        <div className="flex gap-1" data-testid="refund-window-toggle">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                days === d ? "bg-[#A3E635] text-black" : "text-[#E8E4DC] hover:text-white hover:bg-white/10"
              }`}
              data-testid={`refund-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-[#E8E4DC] text-sm" data-testid="refund-loading">Loading…</div>
      ) : !data ? (
        <div className="text-[#E8E4DC]/60 text-sm">Failed to load refund activity.</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-[#E8E4DC]/70 text-xs uppercase tracking-wider mb-1">Refunds</p>
              <p className="text-3xl font-bold text-white" data-testid="refund-count">{data.refund_count}</p>
            </div>
            <div>
              <p className="text-[#E8E4DC]/70 text-xs uppercase tracking-wider mb-1">$ Refunded</p>
              <p className="text-3xl font-bold text-white" data-testid="refund-total-usd">
                ${data.total_refunded_usd.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-[#E8E4DC]/70 text-xs uppercase tracking-wider mb-1">Refund Rate</p>
              <p className={`text-3xl font-bold ${rateColor}`} data-testid="refund-rate-pct">
                {data.refund_rate_pct}%
              </p>
              <p className="text-[#E8E4DC]/50 text-[10px] mt-0.5">
                of {data.paid_in_window + data.refund_count} paid in window
              </p>
            </div>
          </div>

          {data.most_refunded_code && (() => {
            const code = data.most_refunded_code.code;
            const isDisabled = disabledCodes.has(code);
            return (
              <div
                className={`rounded-lg p-3 mb-4 ${
                  isDisabled
                    ? "bg-white/5 border border-white/20"
                    : "bg-amber-400/10 border border-amber-400/30"
                }`}
                data-testid="most-refunded-code"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold mb-1 ${isDisabled ? "text-[#E8E4DC]/70" : "text-amber-400"}`}>
                      {isDisabled ? "✓ Disabled — most-refunded promo code" : "⚠️ Most-refunded promo code"}
                    </p>
                    <p className="text-white text-sm">
                      <span className="font-mono font-bold">{code}</span>
                      <span className="text-[#E8E4DC]/70">
                        {" "}&mdash; refunded {data.most_refunded_code.count} time
                        {data.most_refunded_code.count === 1 ? "" : "s"} in the last {data.window_days} days
                      </span>
                    </p>
                  </div>
                  {isDisabled ? (
                    <button
                      onClick={() => enableCode(code)}
                      disabled={disablingCode === code}
                      className="shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold bg-white/10 text-[#E8E4DC] hover:bg-white/20 hover:text-white transition-colors disabled:opacity-50"
                      data-testid="enable-code-btn"
                      title="Re-enable this code for new redemptions"
                    >
                      {disablingCode === code ? "…" : "Re-enable"}
                    </button>
                  ) : (
                    <button
                      onClick={() => disableCode(code)}
                      disabled={disablingCode === code}
                      className="shrink-0 px-3 py-1.5 rounded-md text-xs font-bold bg-amber-400 text-black hover:bg-amber-300 transition-colors disabled:opacity-50"
                      data-testid="disable-code-btn"
                      title="Block new redemptions of this code. Existing lifetimes are preserved."
                    >
                      {disablingCode === code ? "…" : "Disable code"}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {data.recent.length > 0 ? (
            <div>
              <p className="text-[#E8E4DC]/70 text-xs uppercase tracking-wider mb-2">Recent refunds</p>
              <ul className="space-y-1.5" data-testid="recent-refunds-list">
                {data.recent.map((r, idx) => (
                  <li
                    key={`${r.refunded_at}-${r.email}-${idx}`}
                    className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-b-0"
                  >
                    <span className="text-white truncate flex-1 min-w-0 mr-3">{r.email || "—"}</span>
                    <span className="text-[#E8E4DC]/70 mr-3 whitespace-nowrap">${r.amount?.toFixed?.(2) ?? r.amount}</span>
                    <span className="text-[#E8E4DC]/50 text-xs whitespace-nowrap">{fmtTime(r.refunded_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-[#E8E4DC]/50 text-sm italic" data-testid="no-refunds">
              No refunds in the last {data.window_days} days &mdash; you're cruising. 🎉
            </p>
          )}
        </>
      )}
    </div>
  );
}
